import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import LiquidEther from './LiquidEther';
import ScrollStoryWidePreview, { type WidePreviewKind } from './ScrollStoryWidePreview';
import SiteBeianRecords from '../../components/SiteBeianRecords';
import './ScrollStorySitePage.css';

const WIDE_PREVIEW_BY_CHAPTER_ID: Partial<Record<string, WidePreviewKind>> = {
  'chapter-instruments': 'instruments',
  'chapter-shared-platform': 'sharedPlatform',
  'chapter-consumables': 'consumables',
  'chapter-literature': 'literature',
  'chapter-approval': 'approval',
  'chapter-security': 'security'
};

gsap.registerPlugin(ScrollTrigger);

const BRAND_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/brand/labmind-logo.png`;
const EARTH_HERO_SRC = `${process.env.PUBLIC_URL || ''}/story/earth-hero.png`;
const EARTH_WIDE_BG_SRC = `${process.env.PUBLIC_URL || ''}/story/earth-orbit-wide.png`;

/** 前三个章节（索引 0–2）使用实拍地球 + GSAP 动效 */
const EARTH_PHOTO_LAST_INDEX = 2;

/** 第 4 章至倒数第二章（索引 3…length-2）：宽幅轨道图 + 右上文案与界面卡片；末章收回 */
const WIDE_BG_FROM_INDEX = 3;

const EARTH_STEP_TARGETS: readonly {
  readonly scale: number;
  readonly filter: string;
  readonly objectPosition: string;
}[] = [
  { scale: 0.76, filter: 'brightness(1.02) contrast(1.06) saturate(1.05) blur(0px)', objectPosition: '50% 50%' },
  { scale: 0.9, filter: 'brightness(1.08) contrast(1.08) saturate(1.08) blur(0px)', objectPosition: '52% 48%' },
  { scale: 1.04, filter: 'brightness(1.12) contrast(1.1) saturate(1.12) blur(0px)', objectPosition: '42% 54%' }
];

type ChapterAlign = 'center' | 'left' | 'right';

/** 全屏底色主题：相邻章可复用同一值，用户感知为「背景未变」 */
type BackgroundTheme = 'nebula' | 'ember' | 'void';

/**
 * 中央舞台样式：可与背景解耦——多章可同 `backgroundTheme` 只换 `stageVisual`
 * 占位模拟参考站：地球 / 日食环 / 粒子带 / 粒子环绕 等
 */
type StageVisual = 'globe' | 'eclipse' | 'ribbon' | 'orbit' | 'swarm';

interface StoryChapter {
  readonly id: string;
  readonly eyebrow: string;
  /** 顶栏导航短名（与每一屏一一对应）；缺省用 eyebrow */
  readonly navLabel?: string;
  readonly title: string;
  /** 副文案；首章可省略，仅保留大标题 */
  readonly body?: string;
  readonly accent: string;
  readonly align: ChapterAlign;
  readonly ctaLabel?: string;
  readonly backgroundTheme: BackgroundTheme;
  readonly stageVisual: StageVisual;
}

const CHAPTERS: readonly StoryChapter[] = [
  {
    id: 'chapter-intro',
    eyebrow: 'LabMind',
    navLabel: '开篇',
    title: '把实验室的日常，收敛成一套叙事。',
    accent: '#2997ff',
    align: 'center',
    ctaLabel: '继续',
    backgroundTheme: 'nebula',
    stageVisual: 'globe'
  },
  {
    id: 'chapter-system',
    eyebrow: '实验室',
    navLabel: '系统',
    title: '为实验室而造。',
    body: '轻、准、连贯。每一件琐事都有归处，每一次专注都不被打断。',
    accent: '#2997ff',
    align: 'center',
    ctaLabel: '继续',
    backgroundTheme: 'nebula',
    stageVisual: 'eclipse'
  },
  {
    id: 'chapter-explore',
    eyebrow: '探索',
    navLabel: '浏览',
    title: '向下，走进每一处细节。',
    body: '仪器预约、共享开放平台、耗材管理、文献与笔记、审批与管理，以及安全部署——一项一项，为你展开。',
    accent: '#0071e3',
    align: 'center',
    ctaLabel: '下一章',
    backgroundTheme: 'nebula',
    stageVisual: 'eclipse'
  },
  {
    id: 'chapter-instruments',
    eyebrow: '仪器预约',
    navLabel: '仪器',
    title: '时间与设备，终于对齐。',
    body: '空闲与冲突实时可见；规则与授权一次写清。预约不再是群聊里的接力，而是日历上可托付的一段块。',
    accent: '#0071e3',
    align: 'left',
    ctaLabel: '下一项',
    backgroundTheme: 'ember',
    stageVisual: 'eclipse'
  },
  {
    id: 'chapter-shared-platform',
    eyebrow: '共享平台',
    navLabel: '共享',
    title: '开放预约，也是实验室的「门面」。',
    body:
      '特色能力：既可对本团队外开放仪器预约——链接获客、规则与审批一次配齐，访客按团队码归属、接待与通知一站送达；也可组建多团队共享平台，协约上架、独立计价与时段策略。对内是效率，对外是专业与信任。',
    accent: '#0ea5e9',
    align: 'right',
    ctaLabel: '继续',
    backgroundTheme: 'ember',
    stageVisual: 'eclipse'
  },
  {
    id: 'chapter-consumables',
    eyebrow: '耗材管理',
    navLabel: '耗材',
    title: '每一件物料，都有名字和去向。',
    body: '批次与效期自动靠前；库存与申领一条链路拉通。少一句「谁还有一盒？」的追问。',
    accent: '#06c',
    align: 'right',
    ctaLabel: '继续',
    backgroundTheme: 'ember',
    stageVisual: 'eclipse'
  },
  {
    id: 'chapter-literature',
    eyebrow: '文献与笔记',
    navLabel: '文献',
    title: '读过、写过、用过的，都留得住。',
    body: '论文、方法学与笔记在一处聚合；引用带回原文上下文。知识不再散在文件夹与聊天记录之间。',
    accent: '#5e5ce6',
    align: 'left',
    ctaLabel: '继续',
    backgroundTheme: 'ember',
    stageVisual: 'eclipse'
  },
  {
    id: 'chapter-approval',
    eyebrow: '审批与管理',
    navLabel: '审批',
    title: '流程在线，进展可追踪。',
    body: '从采购到报销、从变更到岗前培训——模板化节点、清晰责任人与可审计记录。管理看得见，却不挡路。',
    accent: '#ac8eff',
    align: 'right',
    ctaLabel: '继续',
    backgroundTheme: 'ember',
    stageVisual: 'eclipse'
  },
  {
    id: 'chapter-security',
    eyebrow: '安全部署',
    navLabel: '安全',
    title: '边界清楚，才安心上云。',
    body: '多团队隔离、细粒度权限与部署拓扑，由统一策略治理。加密、备份与审计日志是默认选项，而非事后补丁。',
    accent: '#64d2ff',
    align: 'center',
    ctaLabel: '开始使用',
    backgroundTheme: 'nebula',
    stageVisual: 'eclipse'
  }
];

/** 末章（安全部署）不收窄幅轨道全屏层，回到星云 + 圆环舞台与 Logo */
const WIDE_BG_LAST_INDEX = CHAPTERS.length - 2;

const CTA_SECTION_ID = 'chapter-cta';

const CTA_COPY = {
  eyebrow: '从这里开始',
  title: '把 LabMind 带进你的实验室。',
  body: '登录即可使用完整能力——含仪器预约、共享开放平台、文献与审批等特色模块。需要账号或部署支持时，从登录页继续即可。',
  align: 'center' as const
};

const alignClass = (a: ChapterAlign) =>
  a === 'left' ? 'scroll-story-copy--left' : a === 'right' ? 'scroll-story-copy--right' : 'scroll-story-copy--center';

const ScrollStorySitePage: React.FC = () => {
  const navigate = useNavigate();
  const lenisRef = useRef<Lenis | null>(null);
  const rafRef = useRef<number>(0);
  const [activeChapter, setActiveChapter] = useState(0);
  /** 与滚动同步：用于背景 / 舞台 */
  const scrollChapterRef = useRef(0);
  /** 文案层展示的章节（可与 active 短暂不一致以做过渡） */
  const [visualChapter, setVisualChapter] = useState(0);
  const reducedMotion = useRef(false);
  const copyRootRef = useRef<HTMLDivElement>(null);
  const textAnimRef = useRef<gsap.core.Tween | null>(null);
  const earthPhotoStackRef = useRef<HTMLDivElement>(null);
  const earthPhotoImgRef = useRef<HTMLImageElement>(null);
  /** 仅在首次进入第 1 章时播放「由暗到明、由远及近」入场 */
  const earthIntroPlayedRef = useRef(false);
  const wideBgRef = useRef<HTMLDivElement>(null);
  const wideBgImgRef = useRef<HTMLImageElement>(null);
  const stageRootRef = useRef<HTMLDivElement>(null);
  const prevScrollForWideRef = useRef(0);
  const [liquidLayerOpen, setLiquidLayerOpen] = useState(false);
  const liquidEtherWrapRef = useRef<HTMLDivElement>(null);

  const scrollToSelector = useCallback((hash: string, instant = false) => {
    const lenis = lenisRef.current;
    const offset = -56;
    if (lenis) {
      lenis.scrollTo(hash, { offset, immediate: instant });
      return;
    }
    const el = document.querySelector(hash);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY + offset;
      window.scrollTo({ top: Math.max(0, top), behavior: instant ? 'auto' : 'smooth' });
    }
  }, []);

  const totalSlides = CHAPTERS.length + 1;
  const scrollIndex = activeChapter >= 0 && activeChapter < totalSlides ? activeChapter : 0;
  const isCtaSlide = scrollIndex === CHAPTERS.length;
  const bgTheme: BackgroundTheme = isCtaSlide ? 'void' : CHAPTERS[scrollIndex].backgroundTheme;
  /** 尾屏与末章一致：圆环 + 中央 Logo，不用 swarm 粒子球 */
  const stageVisual: StageVisual = isCtaSlide ? 'eclipse' : CHAPTERS[scrollIndex].stageVisual;
  const accent = isCtaSlide ? '#2997ff' : CHAPTERS[scrollIndex].accent;

  const earthPhotoMode = !isCtaSlide && scrollIndex <= EARTH_PHOTO_LAST_INDEX;
  const wideBgMode =
    !isCtaSlide && scrollIndex >= WIDE_BG_FROM_INDEX && scrollIndex <= WIDE_BG_LAST_INDEX;
  const finaleStoryChapter = !isCtaSlide && scrollIndex === CHAPTERS.length - 1;
  const stageEarthMode: 'photo' | 'wide' | 'synth' | 'logo' = earthPhotoMode
    ? 'photo'
    : wideBgMode
      ? 'wide'
      : finaleStoryChapter || isCtaSlide
        ? 'logo'
        : 'synth';

  useLayoutEffect(() => {
    scrollChapterRef.current = scrollIndex;
  }, [scrollIndex]);

  useEffect(() => {
    reducedMotion.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.documentElement.classList.add('scroll-story-page');
    document.body.classList.add('scroll-story-page');

    const reduced = reducedMotion.current;
    let lenis: Lenis | null = null;
    const unsubs: Array<() => void> = [];

    if (!reduced) {
      lenis = new Lenis({
        lerp: 0.09,
        smoothWheel: true
      });
      lenisRef.current = lenis;
      const onLenisScroll = () => ScrollTrigger.update();
      lenis.on('scroll', onLenisScroll);
      unsubs.push(() => lenis?.off('scroll', onLenisScroll));

      const raf = (time: number) => {
        lenis?.raf(time);
        rafRef.current = requestAnimationFrame(raf);
      };
      rafRef.current = requestAnimationFrame(raf);
    }

    const triggers: ScrollTrigger[] = [];

    CHAPTERS.forEach((ch, index) => {
      const section = document.getElementById(ch.id);
      if (!section) return;
      triggers.push(
        ScrollTrigger.create({
          trigger: section,
          start: 'top 52%',
          onEnter: () => setActiveChapter(index),
          onEnterBack: () => setActiveChapter(index)
        })
      );
    });

    const ctaEl = document.getElementById(CTA_SECTION_ID);
    if (ctaEl) {
      triggers.push(
        ScrollTrigger.create({
          trigger: ctaEl,
          start: 'top 52%',
          onEnter: () => setActiveChapter(CHAPTERS.length),
          onEnterBack: () => setActiveChapter(CHAPTERS.length)
        })
      );
    }

    ScrollTrigger.refresh();

    return () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      triggers.forEach((t) => t.kill());
      unsubs.forEach((u) => u());
      lenis?.destroy();
      lenisRef.current = null;
      document.documentElement.classList.remove('scroll-story-page');
      document.body.classList.remove('scroll-story-page');
    };
  }, []);

  useEffect(() => {
    if (visualChapter === scrollIndex) return;

    const reduced = reducedMotion.current;
    const root = copyRootRef.current;
    if (reduced || !root) {
      setVisualChapter(scrollIndex);
      return;
    }

    const parts = root.querySelectorAll('.story-anim');
    textAnimRef.current?.kill();
    textAnimRef.current = gsap.to(parts, {
      opacity: 0,
      y: -14,
      filter: 'blur(6px)',
      duration: 0.28,
      stagger: 0.035,
      ease: 'power2.in',
      onComplete: () => {
        setVisualChapter(scrollChapterRef.current);
      }
    });

    return () => {
      textAnimRef.current?.kill();
      textAnimRef.current = null;
    };
  }, [scrollIndex, visualChapter]);

  useLayoutEffect(() => {
    const reduced = reducedMotion.current;
    const root = copyRootRef.current;
    if (!root) return;

    const parts = root.querySelectorAll('.story-anim');
    if (reduced) {
      gsap.set(parts, { opacity: 1, y: 0, filter: 'none', clearProps: 'all' });
      return;
    }

    gsap.set(parts, { opacity: 0, y: 22, filter: 'blur(8px)' });
    gsap.to(parts, {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      duration: 0.55,
      stagger: 0.065,
      ease: 'power3.out',
      delay: 0.04
    });
  }, [visualChapter]);

  useLayoutEffect(() => {
    const wrap = earthPhotoStackRef.current;
    const img = earthPhotoImgRef.current;

    const cleanupEarth = () => {
      const w = earthPhotoStackRef.current;
      const im = earthPhotoImgRef.current;
      if (w && im) gsap.killTweensOf([w, im]);
    };

    if (!wrap || !img) {
      return cleanupEarth;
    }

    if (reducedMotion.current) {
      gsap.killTweensOf([wrap, img]);
      const show = scrollIndex <= EARTH_PHOTO_LAST_INDEX && !isCtaSlide;
      gsap.set(wrap, { clearProps: 'transform,opacity' });
      gsap.set(img, { clearProps: 'filter,objectPosition' });
      wrap.style.opacity = show ? '1' : '0';
      wrap.style.transform = show ? `scale(${EARTH_STEP_TARGETS[scrollIndex]?.scale ?? 1})` : 'scale(1)';
      if (show && EARTH_STEP_TARGETS[scrollIndex]) {
        img.style.filter = EARTH_STEP_TARGETS[scrollIndex].filter;
        img.style.objectPosition = EARTH_STEP_TARGETS[scrollIndex].objectPosition;
      }
      return cleanupEarth;
    }

    if (isCtaSlide || scrollIndex > EARTH_PHOTO_LAST_INDEX) {
      gsap.killTweensOf([wrap, img]);
      gsap.to(wrap, {
        opacity: 0,
        duration: 0.42,
        ease: 'power2.inOut',
        onComplete: () => {
          gsap.set(wrap, { scale: EARTH_STEP_TARGETS[0].scale });
        }
      });
      return cleanupEarth;
    }

    gsap.killTweensOf([wrap, img]);

    const t = EARTH_STEP_TARGETS[scrollIndex];
    const isFirstEverIntro = scrollIndex === 0 && !earthIntroPlayedRef.current;

    if (isFirstEverIntro) {
      gsap.set(wrap, { scale: 0.38, opacity: 0.22 });
      gsap.set(img, { filter: 'brightness(0.12) blur(14px)', objectPosition: '50% 50%' });
      gsap.to(wrap, {
        scale: t.scale,
        opacity: 1,
        duration: 1.48,
        ease: 'power3.out',
        onComplete: () => {
          earthIntroPlayedRef.current = true;
        }
      });
      gsap.to(img, {
        filter: t.filter,
        objectPosition: t.objectPosition,
        duration: 1.42,
        ease: 'power3.out'
      });
      return cleanupEarth;
    }

    gsap.set(wrap, { opacity: 1 });
    const duration = 1.05;
    gsap.to(wrap, { scale: t.scale, duration, ease: 'power2.out' });
    gsap.to(img, {
      filter: t.filter,
      objectPosition: t.objectPosition,
      duration,
      ease: 'power2.out'
    });

    return cleanupEarth;
  }, [scrollIndex, isCtaSlide]);

  useLayoutEffect(() => {
    const wide = wideBgRef.current;
    const stage = stageRootRef.current;
    const wideImg = wideBgImgRef.current;

    const cleanupWide = () => {
      const w = wideBgRef.current;
      const s = stageRootRef.current;
      const wi = wideBgImgRef.current;
      const list = [w, s, wi].filter(Boolean) as HTMLElement[];
      if (list.length) gsap.killTweensOf(list);
    };

    if (!wide || !stage) {
      return cleanupWide;
    }

    const prev = prevScrollForWideRef.current;
    const wideOn =
      !isCtaSlide && scrollIndex >= WIDE_BG_FROM_INDEX && scrollIndex <= WIDE_BG_LAST_INDEX;
    const enteredWide = wideOn && prev < WIDE_BG_FROM_INDEX;

    if (reducedMotion.current) {
      const list = [wide, stage, wideImg].filter(Boolean) as HTMLElement[];
      if (list.length) gsap.killTweensOf(list);
      gsap.set(wide, { opacity: wideOn ? 1 : 0 });
      gsap.set(stage, { opacity: wideOn ? 0 : 1 });
      if (wideImg) gsap.set(wideImg, { scale: 1, clearProps: 'transform' });
      return cleanupWide;
    }

    const list = [wide, stage, wideImg].filter(Boolean) as HTMLElement[];
    gsap.killTweensOf(list);

    if (wideOn) {
      if (enteredWide && wideImg) {
        gsap.set(wideImg, { scale: 1.07, transformOrigin: '8% 50%' });
        gsap.to(wideImg, { scale: 1, duration: 1.38, ease: 'power2.out' });
      }
      gsap.to(wide, { opacity: 1, duration: 1.12, ease: 'power2.inOut' });
      gsap.to(stage, { opacity: 0, duration: 0.92, ease: 'power2.inOut', delay: 0.06 });
    } else {
      gsap.to(wide, { opacity: 0, duration: 0.88, ease: 'power2.inOut' });
      gsap.to(stage, { opacity: 1, duration: 0.82, ease: 'power2.inOut' });
      if (wideImg) gsap.set(wideImg, { scale: 1 });
    }

    prevScrollForWideRef.current = scrollIndex;

    return cleanupWide;
  }, [scrollIndex]);

  useLayoutEffect(() => {
    if (wideBgMode) {
      setLiquidLayerOpen(true);
    }
  }, [wideBgMode]);

  useLayoutEffect(() => {
    const el = liquidEtherWrapRef.current;
    if (!liquidLayerOpen || !el) return;

    if (!wideBgMode) {
      gsap.killTweensOf(el);
      gsap.to(el, {
        opacity: 0,
        duration: 0.55,
        ease: 'power2.in',
        onComplete: () => setLiquidLayerOpen(false)
      });
      return;
    }

    if (reducedMotion.current) {
      gsap.killTweensOf(el);
      gsap.set(el, { opacity: 0.32 });
      return;
    }

    gsap.killTweensOf(el);
    gsap.set(el, { opacity: 0 });
    gsap.to(el, {
      opacity: 0.36,
      duration: 2.35,
      ease: 'power2.out',
      delay: 0.48
    });

    return () => {
      const le = liquidEtherWrapRef.current;
      if (le) gsap.killTweensOf(le);
    };
  }, [liquidLayerOpen, wideBgMode]);

  const vCh = visualChapter >= 0 && visualChapter < CHAPTERS.length ? visualChapter : CHAPTERS.length;
  const showCtaCopy = vCh === CHAPTERS.length;
  const ch = !showCtaCopy ? CHAPTERS[vCh] : null;

  const totalDots = totalSlides;
  const copyAlignForDock = isCtaSlide ? CTA_COPY.align : CHAPTERS[scrollIndex].align;
  const copyDockLayoutClass = wideBgMode
    ? 'scroll-story-copy-dock--rightTop'
    : alignClass(copyAlignForDock);
  const introChapterCopyDock = !isCtaSlide && scrollIndex === 0;
  const widePreviewKind: WidePreviewKind | null =
    !showCtaCopy &&
    ch &&
    vCh >= WIDE_BG_FROM_INDEX &&
    vCh <= WIDE_BG_LAST_INDEX
      ? WIDE_PREVIEW_BY_CHAPTER_ID[ch.id] ?? null
      : null;

  const year = new Date().getFullYear();

  return (
    <div className="scroll-story-root" data-wide-bg={wideBgMode ? '1' : '0'} data-cta-slide={isCtaSlide ? '1' : '0'}>
      <div className="scroll-story-fixed-bg" aria-hidden data-theme={bgTheme} />

      <div ref={wideBgRef} className="scroll-story-wide-bg" aria-hidden>
        <img
          ref={wideBgImgRef}
          className="scroll-story-wide-bg-img"
          src={EARTH_WIDE_BG_SRC}
          alt=""
          width={1920}
          height={1080}
          decoding="async"
        />
        {liquidLayerOpen && (
          <div ref={liquidEtherWrapRef} className="scroll-story-wide-liquid-wrap">
            <LiquidEther
              className="scroll-story-wide-liquid-ether"
              colors={['#5227FF', '#FF9FFC', '#B19EEF']}
              mouseForce={20}
              cursorSize={100}
              isViscous
              viscous={30}
              iterationsViscous={32}
              iterationsPoisson={32}
              resolution={0.5}
              isBounce={false}
              autoDemo
              autoSpeed={0.5}
              autoIntensity={2.2}
              takeoverDuration={0.25}
              autoResumeDelay={3000}
              autoRampDuration={0.6}
            />
          </div>
        )}
        <div className="scroll-story-wide-bg-scrim" />
      </div>

      <div
        ref={stageRootRef}
        className="scroll-story-fixed-stage"
        aria-hidden
        data-visual={stageVisual}
        data-earth={stageEarthMode}
        style={{ ['--stage-accent' as string]: accent }}
      >
        <div className="scroll-story-globe">
          <div ref={earthPhotoStackRef} className="scroll-story-earth-photo-stack">
            <img
              ref={earthPhotoImgRef}
              className="scroll-story-earth-photo-img"
              src={EARTH_HERO_SRC}
              alt=""
              width={900}
              height={900}
              decoding="async"
            />
            <div className="scroll-story-earth-photo-rim" aria-hidden />
          </div>
          <div className="scroll-story-globe-logo" aria-hidden>
            <img src={BRAND_LOGO_SRC} alt="" width={112} height={74} decoding="async" />
          </div>
          <div className="scroll-story-globe-shade" />
          <div className="scroll-story-globe-atmos" />
        </div>
        <div className="scroll-story-eclipse-ring" />
        <div className="scroll-story-particle-ribbon" />
        <div className="scroll-story-particle-orbit">
          {Array.from({ length: 48 }).map((_, i) => (
            <span key={i} className="scroll-story-orbit-dot" style={{ ['--i' as string]: String(i) }} />
          ))}
        </div>
        <div className="scroll-story-particle-swarm">
          {Array.from({ length: 36 }).map((_, i) => (
            <span key={i} className="scroll-story-swarm-dot" style={{ ['--j' as string]: String(i) }} />
          ))}
        </div>
      </div>

      <header className="scroll-story-nav scroll-story-nav--bare">
        <button type="button" className="scroll-story-nav-brand" onClick={() => scrollToSelector(`#${CHAPTERS[0].id}`)} aria-label="LabMind，回到首屏">
          <img className="scroll-story-nav-logo" src={BRAND_LOGO_SRC} alt="" width={112} height={74} decoding="async" />
          <span className="scroll-story-nav-title">LabMind</span>
        </button>
        <nav className="scroll-story-nav-links" aria-label="章节">
          {CHAPTERS.map((c, i) => (
            <button
              key={c.id}
              type="button"
              className={scrollIndex === i ? 'is-active' : undefined}
              aria-current={scrollIndex === i ? 'page' : undefined}
              onClick={() => scrollToSelector(`#${c.id}`)}
            >
              {c.navLabel ?? c.eyebrow}
            </button>
          ))}
          <button
            type="button"
            className={scrollIndex === CHAPTERS.length ? 'is-active' : undefined}
            aria-current={scrollIndex === CHAPTERS.length ? 'page' : undefined}
            onClick={() => scrollToSelector(`#${CTA_SECTION_ID}`)}
          >
            开始
          </button>
        </nav>
        <button type="button" className="scroll-story-nav-cta" onClick={() => navigate('/')}>
          登录 / 注册
        </button>
      </header>

      <div className="scroll-story-rail" role="tablist" aria-label="章节进度">
        {Array.from({ length: totalDots }).map((_, i) => (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={scrollIndex === i}
            aria-label={i < CHAPTERS.length ? `第 ${i + 1} 章` : '结尾行动'}
            className={scrollIndex === i ? 'is-active' : ''}
            onClick={() => scrollToSelector(i < CHAPTERS.length ? `#${CHAPTERS[i].id}` : `#${CTA_SECTION_ID}`)}
          />
        ))}
      </div>

      <div
        ref={copyRootRef}
        className={`scroll-story-copy-dock ${copyDockLayoutClass}${introChapterCopyDock ? ' scroll-story-copy-dock--introTop' : ''}`}
        style={{ ['--chapter-accent' as string]: showCtaCopy ? '#2997ff' : ch?.accent ?? '#2997ff' }}
        aria-live="polite"
      >
        {showCtaCopy ? (
          <>
            <p className="scroll-story-eyebrow story-anim">{CTA_COPY.eyebrow}</p>
            <h2 className="story-anim">{CTA_COPY.title}</h2>
            <p className="scroll-story-body story-anim">{CTA_COPY.body}</p>
            <div className="scroll-story-cta-row story-anim">
              <button type="button" className="scroll-story-cta-primary" onClick={() => navigate('/')}>
                进入系统
              </button>
              <button type="button" className="scroll-story-ghost" onClick={() => navigate('/')}>
                返回登录
              </button>
            </div>
          </>
        ) : (
          ch && (
            <>
              <p className="scroll-story-eyebrow story-anim">{ch.eyebrow}</p>
              <h2 className="story-anim">{ch.title}</h2>
              {ch.body ? <p className="scroll-story-body story-anim">{ch.body}</p> : null}
              {widePreviewKind ? <ScrollStoryWidePreview kind={widePreviewKind} accent={ch.accent} /> : null}
              <button
                type="button"
                className="scroll-story-ghost story-anim"
                onClick={() => scrollToSelector(vCh + 1 < CHAPTERS.length ? `#${CHAPTERS[vCh + 1].id}` : `#${CTA_SECTION_ID}`)}
              >
                {ch.ctaLabel ?? '继续'}
                <span className="scroll-story-ghost-arrow" aria-hidden>
                  ↗
                </span>
              </button>
            </>
          )
        )}
      </div>

      {scrollIndex === 0 && (
        <div className="scroll-story-scroll-hint" aria-hidden>
          <span>Scroll</span>
          <div className="scroll-story-mouse" />
        </div>
      )}

      {isCtaSlide ? (
        <footer className="scroll-story-site-footer" role="contentinfo">
          <div className="scroll-story-site-footer__inner">
            <p className="scroll-story-site-footer__meta">
              © {year} LabMind。保留所有权利。
            </p>
            <nav className="scroll-story-site-footer__links" aria-label="法律与联系">
              <a href="#" onClick={(e) => e.preventDefault()}>
                隐私政策
              </a>
              <span className="scroll-story-site-footer__sep" aria-hidden>
                ·
              </span>
              <a href="#" onClick={(e) => e.preventDefault()}>
                使用条款
              </a>
              <span className="scroll-story-site-footer__sep" aria-hidden>
                ·
              </span>
              <a href="mailto:hello@labmind.io">联系我们</a>
            </nav>
            <SiteBeianRecords className="scroll-story-site-footer__beian" />
          </div>
        </footer>
      ) : null}

      <main className="scroll-story-main">
        {CHAPTERS.map((chap) => (
          <section key={chap.id} id={chap.id} className="scroll-story-spacer" aria-label={chap.eyebrow}>
            <span className="scroll-story-sr-only">
              {chap.title}
              {chap.body ? ` ${chap.body}` : ''}
            </span>
          </section>
        ))}
        <section id={CTA_SECTION_ID} className="scroll-story-spacer scroll-story-spacer--cta" aria-label="行动">
          <span className="scroll-story-sr-only">
            {CTA_COPY.title} {CTA_COPY.body}
          </span>
        </section>
      </main>
    </div>
  );
};

export default ScrollStorySitePage;
